import React, { useEffect, useState, useMemo } from "react";
import { api, formatEUR } from "@/lib/api";
import { toast } from "sonner";
import { BarChart3, FileDown, AlertTriangle, Package, Percent, Info } from "lucide-react";
import { useStore } from "@/lib/storeContext";
import { Pagination } from "@/components/Pagination";
import { getPageSize, setPageSize, pageSlice } from "@/lib/listDensity";

const VAT_LABEL = { 4: "4%", 5: "5%", 10: "10%", 22: "22%" };

export default function SalesAnalytics() {
  const { storeId } = useStore();
  const now = new Date();
  const defaultMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const [month, setMonth] = useState(defaultMonth);
  const [tab, setTab] = useState("department");
  const [prodPage, setProdPage] = useState(1);
  const [pageSize, setSize] = useState(getPageSize);
  const [data, setData] = useState(null);
  const [lowStock, setLowStock] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = () => {
    setLoading(true); setError(null);
    const [y, m] = month.split("-");
    const from = `${y}-${m}-01T00:00:00`;
    const nextY = m === "12" ? Number(y) + 1 : Number(y);
    const nextM = m === "12" ? "01" : String(Number(m) + 1).padStart(2, "0");
    const to = `${nextY}-${nextM}-01T00:00:00`;
    Promise.all([
      api.get(`/reports/sales-analytics?from_date=${encodeURIComponent(from)}&to_date=${encodeURIComponent(to)}${storeId ? `&store_id=${storeId}` : ""}`),
      api.get(`/reports/low-stock`),
    ])
      .then(([sales, stock]) => {
        setData(sales.data);
        setLowStock(stock.data.items || []);
      })
      .catch((e) => setError(e?.response?.data?.detail || "Errore caricamento"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [month, storeId]);

  const exportCSV = () => {
    if (!data) return;
    let rows = [];
    if (tab === "department") {
      rows = [["Reparto RT", "Aliquota IVA", "Incasso lordo €", "IVA €", "Pezzi", "N. righe"]];
      for (const r of data.by_department) {
        rows.push([r.department, `${r.vat_rate}%`, r.revenue.toFixed(2), r.vat_amount.toFixed(2), r.units, r.count]);
      }
    } else if (tab === "vat") {
      rows = [["Aliquota IVA", "Incasso lordo €", "Netto €", "IVA €"]];
      for (const r of data.by_vat) {
        rows.push([`${r.vat_rate}%`, r.revenue_gross.toFixed(2), r.revenue_net.toFixed(2), r.vat_amount.toFixed(2)]);
      }
    } else {
      rows = [["Articolo", "Reparto", "IVA", "Pezzi venduti", "Incasso €", "Scorta", "Soglia min", "Sotto soglia"]];
      for (const r of data.by_product) {
        rows.push([r.name, r.department || "auto", `${r.vat_rate}%`, r.units, r.revenue.toFixed(2), r.stock_current ?? "—", r.stock_limit ?? "—", r.low_stock ? "SÌ" : ""]);
      }
    }
    const csv = (Array.isArray(rows) ? rows : []).map((r) => (Array.isArray(r) ? r : []).map((c) => `"${String(c).replace(/"/g, '""')}"`).join(";")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `vendite-${tab}-${month}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const topProduct = useMemo(() => data?.by_product?.[0], [data]);

  if (error) {
    return (
      <div className="p-8 lg:p-12 max-w-6xl">
        <div className="bg-red-50 border border-red-200 rounded p-4 flex gap-3">
          <Info className="w-5 h-5 text-red-700" />
          <div className="text-sm text-red-900">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 lg:p-12 max-w-6xl">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.25em] text-velta-mute mb-3">Report vendite</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight flex items-center gap-3">
            <BarChart3 className="w-8 h-8 text-[#2F9E77]" /> Analisi Vendite
          </h1>
          <p className="text-velta-mute mt-2 max-w-2xl">
            Incasso scorporato per <strong>reparto della stampante fiscale RT</strong>, per aliquota IVA e per articolo. Con avvisi scorta minima.
          </p>
        </div>
        <div className="flex items-end gap-3">
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Mese</label>
            <input
              data-testid="sales-month"
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="h-11 px-3 border border-velta-border rounded-md font-mono focus:border-[#1E2D5B] outline-none"
            />
          </div>
          <button
            data-testid="sales-export"
            onClick={exportCSV}
            disabled={!data}
            className="h-11 px-4 bg-white border border-velta-border hover:border-velta-ink text-velta-ink rounded-md text-sm font-medium inline-flex items-center gap-2 btn-tactile disabled:opacity-40"
          >
            <FileDown className="w-4 h-4" /> Esporta CSV
          </button>
        </div>
      </div>

      {/* Low stock banner */}
      {lowStock.length > 0 && (
        <div className="mb-6 bg-yellow-50 border-2 border-yellow-300 rounded-md p-4 flex items-start gap-3">
          <AlertTriangle className="w-6 h-6 text-yellow-700 shrink-0" />
          <div className="flex-1">
            <div className="font-heading font-semibold text-yellow-900">
              {lowStock.length} articol{lowStock.length === 1 ? "o" : "i"} sotto soglia minima
            </div>
            <div className="text-sm text-yellow-800 mt-1">
              {(Array.isArray(lowStock) ? lowStock : []).slice(0, 8).map((p) => (
                <span key={p.id} className="inline-block mr-3 mb-1">
                  <strong>{p.name}</strong> ({p.stock_current}/{p.stock_limit})
                </span>
              ))}
              {lowStock.length > 8 && <span>… e altri {lowStock.length - 8}</span>}
            </div>
            <div className="text-xs text-yellow-700 mt-2">
              Rifornisci il magazzino in <a href="/dashboard/magazzino" className="underline font-medium">Magazzino</a> o rivedi la soglia in <a href="/dashboard/articoli" className="underline font-medium">Articoli</a>.
            </div>
          </div>
        </div>
      )}

      {loading || !data ? (
        <div className="text-center text-velta-mute py-16 text-sm">Caricamento…</div>
      ) : (
        <>
          {/* Totals */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <div className="bg-white border border-velta-border rounded-md p-4">
              <div className="text-[10px] font-mono uppercase tracking-widest text-velta-mute">Scontrini</div>
              <div data-testid="sales-total-receipts" className="font-heading text-3xl font-bold mt-1">{data.totals.receipts}</div>
              {(data.totals.refunds || 0) > 0 && (
                <div data-testid="sales-total-refunds" className="text-[10px] font-mono text-red-600 mt-1">Storni {data.totals.refunds} · {formatEUR(data.totals.refunds_amount || 0)}</div>
              )}
            </div>
            <div className="bg-white border-2 border-velta-ink rounded-md p-4">
              <div className="text-[10px] font-mono uppercase tracking-widest text-velta-mute">Incasso lordo</div>
              <div data-testid="sales-total-gross" className="font-heading text-3xl font-bold mt-1 tabular-nums">{formatEUR(data.totals.revenue_gross)}</div>
            </div>
            <div className="bg-white border border-velta-border rounded-md p-4">
              <div className="text-[10px] font-mono uppercase tracking-widest text-velta-mute">Netto (IVA escl.)</div>
              <div className="font-heading text-3xl font-bold mt-1 tabular-nums">{formatEUR(data.totals.revenue_net)}</div>
              <div className="text-[10px] font-mono text-velta-mute mt-1">di cui IVA: {formatEUR(data.totals.vat_total)}</div>
            </div>
            <div className="bg-white border border-velta-border rounded-md p-4">
              <div className="text-[10px] font-mono uppercase tracking-widest text-velta-mute">Pezzi venduti</div>
              <div className="font-heading text-3xl font-bold mt-1">{data.totals.units}</div>
              {topProduct && (
                <div className="text-[10px] font-mono text-velta-mute mt-1 truncate">Top: {topProduct.name}</div>
              )}
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-4 border-b border-velta-border">
            {[
              { id: "department", label: "Per Reparto RT", icon: Package },
              { id: "vat", label: "Per Aliquota IVA", icon: Percent },
              { id: "product", label: "Per Articolo", icon: BarChart3 },
            ].map((t) => {
              const I = t.icon;
              return (
                <button
                  key={t.id}
                  data-testid={`sales-tab-${t.id}`}
                  onClick={() => setTab(t.id)}
                  className={`h-11 px-4 font-medium text-sm flex items-center gap-2 transition-colors ${
                    tab === t.id
                      ? "text-velta-ink border-b-2 border-velta-ink -mb-px"
                      : "text-velta-mute hover:text-velta-ink"
                  }`}
                >
                  <I className="w-4 h-4" /> {t.label}
                </button>
              );
            })}
          </div>

          {/* Content */}
          {tab === "department" && (
            <div className="bg-white border border-velta-border rounded-md overflow-x-auto pos-scroll">
              <table className="w-full text-sm min-w-[720px]">
                <thead className="text-left text-xs font-mono uppercase tracking-widest text-velta-mute bg-velta-bg border-b border-velta-border">
                  <tr>
                    <th className="px-4 py-3">Reparto RT</th>
                    <th className="px-4 py-3">Aliquota</th>
                    <th className="px-4 py-3 text-right">Incasso lordo</th>
                    <th className="px-4 py-3 text-right">IVA</th>
                    <th className="px-4 py-3 text-right">Pezzi</th>
                    <th className="px-4 py-3 text-right">N. righe</th>
                  </tr>
                </thead>
                <tbody>
                  {data.by_department.length === 0 && <tr><td colSpan={6} className="px-4 py-12 text-center text-velta-mute">Nessuna vendita nel periodo.</td></tr>}
                  {(Array.isArray(data.by_department) ? data.by_department : []).map((r, i) => (
                    <tr key={i} className="border-t border-velta-border/50 hover:bg-velta-bg/30">
                      <td className="px-4 py-3 font-mono font-bold">Reparto {r.department}</td>
                      <td className="px-4 py-3 font-mono">{r.vat_rate}%</td>
                      <td className="px-4 py-3 font-mono font-bold text-right tabular-nums">{formatEUR(r.revenue)}</td>
                      <td className="px-4 py-3 font-mono text-right tabular-nums text-velta-mute">{formatEUR(r.vat_amount)}</td>
                      <td className="px-4 py-3 font-mono text-right">{r.units}</td>
                      <td className="px-4 py-3 font-mono text-right text-velta-mute">{r.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {tab === "vat" && (
            <div className="bg-white border border-velta-border rounded-md overflow-x-auto pos-scroll">
              <table className="w-full text-sm min-w-[720px]">
                <thead className="text-left text-xs font-mono uppercase tracking-widest text-velta-mute bg-velta-bg border-b border-velta-border">
                  <tr>
                    <th className="px-4 py-3">Aliquota IVA</th>
                    <th className="px-4 py-3 text-right">Incasso lordo</th>
                    <th className="px-4 py-3 text-right">Netto (imponibile)</th>
                    <th className="px-4 py-3 text-right">IVA</th>
                  </tr>
                </thead>
                <tbody>
                  {data.by_vat.length === 0 && <tr><td colSpan={4} className="px-4 py-12 text-center text-velta-mute">Nessuna vendita nel periodo.</td></tr>}
                  {(Array.isArray(data.by_vat) ? data.by_vat : []).map((r, i) => (
                    <tr key={i} className="border-t border-velta-border/50">
                      <td className="px-4 py-3 font-heading font-bold">{VAT_LABEL[r.vat_rate] || `${r.vat_rate}%`}</td>
                      <td className="px-4 py-3 font-mono font-bold text-right tabular-nums">{formatEUR(r.revenue_gross)}</td>
                      <td className="px-4 py-3 font-mono text-right tabular-nums">{formatEUR(r.revenue_net)}</td>
                      <td className="px-4 py-3 font-mono text-right tabular-nums text-velta-mute">{formatEUR(r.vat_amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {tab === "product" && (
            <div className="bg-white border border-velta-border rounded-md overflow-x-auto pos-scroll">
              <table className="w-full text-sm min-w-[720px]">
                <thead className="text-left text-xs font-mono uppercase tracking-widest text-velta-mute bg-velta-bg border-b border-velta-border">
                  <tr>
                    <th className="px-4 py-3">Articolo</th>
                    <th className="px-4 py-3">Reparto / IVA</th>
                    <th className="px-4 py-3 text-right">Pezzi</th>
                    <th className="px-4 py-3 text-right">Incasso</th>
                    <th className="px-4 py-3 text-right">Scorta</th>
                  </tr>
                </thead>
                <tbody>
                  {(Array.isArray(data.by_product) ? data.by_product : []).length === 0 && <tr><td colSpan={5} className="px-4 py-12 text-center text-velta-mute">Nessuna vendita.</td></tr>}
                  {pageSlice(data.by_product, prodPage, pageSize).items.map((r, i) => (
                    <tr key={i} className={`border-t border-velta-border/50 ${r.low_stock ? "bg-yellow-50/40" : ""}`}>
                      <td className="px-4 py-3 font-medium">
                        {r.name}
                        {r.low_stock && (
                          <span className="ml-2 text-[10px] font-mono uppercase tracking-widest text-yellow-800 bg-yellow-100 border border-yellow-300 rounded px-1.5 py-0.5">Scorta bassa</span>
                        )}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-velta-mute">RT {r.department || "auto"} · IVA {r.vat_rate}%</td>
                      <td className="px-4 py-3 font-mono text-right">{r.units}</td>
                      <td className="px-4 py-3 font-mono font-bold text-right tabular-nums">{formatEUR(r.revenue)}</td>
                      <td className="px-4 py-3 font-mono text-right text-xs">
                        {r.stock_current != null ? (
                          <span className={r.low_stock ? "text-yellow-800 font-bold" : ""}>
                            {r.stock_current}/{r.stock_limit ?? "∞"}
                          </span>
                        ) : (
                          <span className="text-velta-mute">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {(data.by_product || []).length > pageSize && (
                <div className="px-4 pb-4">
                  <Pagination
                    page={pageSlice(data.by_product, prodPage, pageSize).page}
                    pages={pageSlice(data.by_product, prodPage, pageSize).pages}
                    total={data.by_product.length}
                    onPage={setProdPage}
                    pageSize={pageSize}
                    onPageSize={(n) => { setSize(setPageSize(n)); setProdPage(1); }}
                  />
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
